import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  Text,
  Link,
  useToast,
  Heading,
  Divider,
  FormErrorMessage,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useApi } from '@/contexts/ApiContext';

export const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    referral_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { colorMode } = useThemeContext();
  const toast = useToast();
  const navigate = useNavigate();
  const api = useApi();
  const { login } = useAuth();

  const isDark = colorMode === 'dark';

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.username) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const res = await api.UserApi.signUp({
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        username: formData.username,
        referral_code: formData.referral_code || undefined,
      });
      toast({
        title: 'Registration successful!',
        description: 'You have been successfully registered. Please sign in.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/login');
    } catch (error: any) {
      toast({
        title: 'Registration failed',
        description: error?.response?.data?.message || error.message || 'Please check your data and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box 
      minH="100vh" 
      bg={isDark ? 'gray.900' : 'gray.50'}
      bgGradient={isDark 
        ? 'linear(to-br, gray.900, blue.900, purple.900)' 
        : 'linear(to-br, blue.50, purple.50, pink.50)'
      }
      py={8}
      position="relative"
    >
      {/* Theme Toggle */}
      <Box position="absolute" top={4} right={4}>
        <ThemeToggle size="md" variant="outline" />
      </Box>
      
      <Container maxW="md" centerContent>
        <VStack spacing={8} w="full">
          {/* Logo/Brand */}
          <VStack spacing={2}>
            <Heading 
              size="xl" 
              bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
              bgClip="text"
              fontWeight="bold"
            >
              Casino Pro
            </Heading>
            <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="lg">
              Create your account to get started.
            </Text>
          </VStack>

          <Card 
            w="full" 
            bg={isDark ? 'gray.800' : 'white'}
            shadow="xl" 
            borderRadius="2xl"
            border="1px"
            borderColor={isDark ? 'gray.600' : 'gray.200'}
          >
            <CardBody p={8}>
              <form onSubmit={handleRegister}>
                <VStack spacing={6}>
                  <VStack spacing={4} w="full">
                    {/* Account Information */}
                    <FormControl isRequired isInvalid={!!errors.email}>
                      <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="john@example.com"
                        size="lg"
                        bg={isDark ? 'gray.700' : 'white'}
                        border="2px"
                        borderColor={isDark ? 'gray.600' : 'gray.200'}
                        color={isDark ? 'gray.300' : 'gray.800'}
                        _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                      />
                      <FormErrorMessage>{errors.email}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!errors.username}>
                      <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Username</FormLabel>
                      <Input
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        placeholder="johndoe"
                        size="lg"
                        bg={isDark ? 'gray.700' : 'white'}
                        border="2px"
                        borderColor={isDark ? 'gray.600' : 'gray.200'}
                        color={isDark ? 'gray.300' : 'gray.800'}
                        _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                      />
                      <FormErrorMessage>{errors.username}</FormErrorMessage>
                    </FormControl>

                    {/* Password Fields */}
                    <FormControl isRequired isInvalid={!!errors.password}>
                      <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          placeholder="Create a strong password"
                          size="lg"
                          bg={isDark ? 'gray.700' : 'white'}
                          border="2px"
                          borderColor={isDark ? 'gray.600' : 'gray.200'}
                          color={isDark ? 'gray.300' : 'gray.800'}
                          _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                        />
                        <InputRightElement h="full">
                          <IconButton
                            variant="ghost"
                            onClick={() => setShowPassword(!showPassword)}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            aria-label="Toggle password visibility"
                            color={isDark ? 'gray.400' : 'gray.600'}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{errors.password}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!errors.confirmPassword}>
                      <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Confirm Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          placeholder="Confirm your password"
                          size="lg"
                          bg={isDark ? 'gray.700' : 'white'}
                          border="2px"
                          borderColor={isDark ? 'gray.600' : 'gray.200'}
                          color={isDark ? 'gray.300' : 'gray.800'}
                          _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                        />
                        <InputRightElement h="full">
                          <IconButton
                            variant="ghost"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                            aria-label="Toggle confirm password visibility"
                            color={isDark ? 'gray.400' : 'gray.600'}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                    </FormControl>

                    {/* Referral Code */}
                    <FormControl>
                      <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Referral Code (Optional)</FormLabel>
                      <Input
                        value={formData.referral_code}
                        onChange={(e) => handleInputChange('referral_code', e.target.value)}
                        placeholder="Enter referral code"
                        size="lg"
                        bg={isDark ? 'gray.700' : 'white'}
                        border="2px"
                        borderColor={isDark ? 'gray.600' : 'gray.200'}
                        color={isDark ? 'gray.300' : 'gray.800'}
                        _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                      />
                    </FormControl>
                  </VStack>

                  <Button
                    type="submit"
                    size="lg"
                    w="full"
                    bgGradient="linear(to-r, green.400, blue.500)"
                    color="white"
                    isLoading={isLoading}
                    loadingText="Creating account..."
                    _hover={{
                      bgGradient: 'linear(to-r, green.500, blue.600)',
                      transform: 'translateY(-2px)',
                      shadow: 'lg'
                    }}
                    transition="all 0.2s"
                  >
                    Create Account
                  </Button>

                  <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />

                  {/* Footer Link */}
                  <HStack spacing={1}>
                    <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="sm">
                      Already have an account?
                    </Text>
                    <Link 
                      as={RouterLink} 
                      to="/login" 
                      color="blue.500"
                      fontWeight="medium"
                      _hover={{ color: 'blue.400' }}
                    >
                      Sign in
                    </Link>
                  </HStack>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
}; 